import type { ReactNode } from 'react';
import { BrandWordmark } from '../../components/BrandWordmark.tsx';
import { LocaleSwitcher, useT } from '../../i18n/index.ts';
import { cx } from '../../ui/index.ts';

/**
 * Public-page frame for the auth suite + invite — Field Notes (epic
 * #950 PR-2). Replaces the console-shared `createAuthPageFrame` factory
 * instantiation: these routes render OUTSIDE the Shell, so the frame
 * owns the full-viewport `canvas` backdrop and centers one paper card.
 *
 * Anatomy: locale switcher on the canvas above the card, BrandWordmark
 * masthead on the card, serif `title`-tier heading (exactly one `h1`
 * per page), caption-tier description, then the page body. The legacy
 * 10px uppercase "eyebrow" pill idiom dies with the old frame (ghost
 * labels are banned, DESIGN.md §3).
 *
 * The switcher lives here rather than in the Shell for these routes:
 * auth pages render outside the Shell, so a visitor who cannot read
 * English would otherwise have no way to reach the Chinese catalog.
 */

type FrameWidth = 'md' | 'lg';

const WIDTH_CLASSES: Record<FrameWidth, string> = {
  md: 'max-w-[420px]',
  lg: 'max-w-[520px]',
};

interface Props {
  readonly title: string;
  readonly description?: string;
  /** Secondary cross-links rendered under the card (register / forgot…). */
  readonly footer?: ReactNode;
  readonly width?: FrameWidth;
  readonly testId?: string;
  readonly children: ReactNode;
}

export function AuthPageFrame({
  title,
  description,
  footer,
  width = 'md',
  testId,
  children,
}: Props): JSX.Element {
  const t = useT();
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-6 sm:p-8">
      <main className={cx('flex w-full flex-col gap-4', WIDTH_CLASSES[width])} data-testid={testId}>
        {/* `px-2` is deliberate, and shared with the footer row below: both
            sit on the canvas outside the card, and the inset keeps them off
            the viewport edge on narrow screens without pretending to align
            with the card's own `p-6`/`sm:p-8`. */}
        <div className="flex justify-end px-2">
          <LocaleSwitcher />
        </div>
        <div className="rounded-xl border border-line bg-card p-6 text-ink sm:p-8">
          <BrandWordmark className="mb-6" descriptor={t('shell.brand.subtitle')} />
          <h1 className="m-0 font-display text-title text-ink">{title}</h1>
          {description !== undefined ? (
            <p className="mb-0 mt-1.5 text-caption text-ink-low">{description}</p>
          ) : null}
          <div className="mt-6">{children}</div>
        </div>
        {footer !== undefined && footer !== null ? (
          <div className="flex flex-col items-start gap-2 px-2">{footer}</div>
        ) : null}
      </main>
    </div>
  );
}

/**
 * Text cross-link inside/under an auth card ("Back to sign in",
 * "Register"…). Caption tier, darkens to `ink` on hover, forest focus
 * ring — the same quiet-link language the Shell uses for secondary
 * navigation.
 */
export const AUTH_LINK_CLASS =
  'rounded text-caption text-ink-low transition-colors duration-150 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40';
